<script>
export default {
    // vue-meta evaluates page() through a watcher that cannot see <script setup> bindings, so
    // the channel is looked up here from the store — app-initializer fills that list at startup
    // — rather than read off the copy setup fetches for the hero.
    page() {
        const channel =
            this.$store.state.channels.channels.find(
                item => item.id === this.$route.params.channelId
            ) || {}
        const channelTitle = channel.title || 'Channel'
        const title = `${channelTitle} Radio`
        const description = channel.description || `Listen to ${channelTitle} on FRISKY Radio.`
        const image = `${import.meta.env.VITE_CLIENT_URL}/frisky-social-share-image.png`
        const canonicalUrl = `https://frisky.fm${this.$route.path}`
        return {
            title,
            link: [{ rel: 'canonical', vmid: 'canonical', href: canonicalUrl }],
            meta: [
                { name: 'title', vmid: 'title', content: title },
                { name: 'description', vmid: 'description', content: description },

                { property: 'og:title', vmid: 'og:title', content: title },
                { property: 'og:description', vmid: 'og:description', content: description },
                { property: 'og:image', vmid: 'og:image', content: image },
                { property: 'og:type', vmid: 'og:type', content: 'website' },
                { property: 'og:url', vmid: 'og:url', content: canonicalUrl },
                { property: 'og:site_name', vmid: 'og:site_name', content: 'FRISKY' },

                { name: 'twitter:card', vmid: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', vmid: 'twitter:title', content: title },
                { name: 'twitter:description', vmid: 'twitter:description', content: description },
                { name: 'twitter:image', vmid: 'twitter:image', content: image },
                { name: 'twitter:url', vmid: 'twitter:url', content: canonicalUrl },
            ],
        }
    },
}
</script>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { useStore } from '@composables/use-store'
import DataLayer from '@services/data-layer/index'
import Layout from '@layouts/main-layout'

import Overview from '@views/channels/channel.overview'
import Schedule from '@views/channels/channel.schedule'
import History from '@views/channels/channel.history'

// `<component :is>` resolves a STRING against registered components, never against
// <script setup> bindings — the routes name the tab, so the name is mapped back here.
const MAIN_COMPONENTS = { Overview, Schedule, History }

const props = defineProps({
    mainComponent: {
        type: String,
        default: 'Overview',
    },
})

const route = useRoute()
const router = useRouter()
const store = useStore()

const loadedChannel = ref({})

const mainView = computed(() => MAIN_COMPONENTS[props.mainComponent])

const nowPlaying = computed(() =>
    store.getters['nowPlaying/currentForStation'](route.params.channelId)
)

const tabs = computed(() => {
    const channelId = route.params.channelId
    const overview = { to: { name: 'channel', params: { channelId } }, label: 'Overview' }
    const schedule = {
        to: { name: 'channel/schedule', params: { channelId } },
        label: 'Show Schedule',
    }
    const history = { to: { name: 'channel/history', params: { channelId } }, label: 'History' }

    return channelId === 'classics' ? [overview, history] : [overview, schedule, history]
})

const resource = computed(() => ({
    ...loadedChannel.value,
    type: 'channel',
    nowPlaying: nowPlaying.value,
}))

const playback = computed(() => store.state.playback)

const status = computed(() => playback.value?.status)

function handleUrlPlayParam() {
    if (
        status.value === 'playing' &&
        playback.value?.resource?.id === loadedChannel.value.id &&
        playback.value?.resource?.type === 'channel'
    ) {
        router.replace({ query: { ...route.query, play: 'true' } })
    } else {
        const { play, ...rest } = route.query
        if (JSON.stringify(rest) === JSON.stringify(route.query)) return
        router.replace({ query: rest })
    }
}

async function loadData() {
    const channels = await DataLayer.channels.get()

    loadedChannel.value = Object.values(channels.body).find(
        channel => channel.id === route.params.channelId
    )

    handleUrlPlayParam()
}

watch(status, handleUrlPlayParam)

onMounted(loadData)
</script>

<template>
    <Layout>
        <BaseHero
            :resource="resource"
            keep-alive
        />
        <BaseTabPanel
            :tabs="tabs"
            address-navigation
        />
        <component
            :is="mainView"
            :channel="resource"
            :now-playing="nowPlaying"
        />
    </Layout>
</template>
